// src/components/dashboard/ParkingStatus.jsx - Updated with clickable campata boxes
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Car, MapPin } from "lucide-react";

export default function ParkingStatus({ tettoie, sezioni, campate, allCars, isLoading, onCampataClick }) {
  if (isLoading) {
    return (
      <Card className="glass-effect border-0 shadow-xl">
        <CardHeader className="p-6 border-b border-slate-100">
          <CardTitle className="text-lg font-bold text-slate-800">
            Stato Parcheggio
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {Array(12).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full rounded-lg" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Count cars per campata
  const getCarCountForCampata = (campataId) => {
    if (!allCars || !Array.isArray(allCars)) return 0;
    return allCars.filter(car => car.campata_id === campataId).length;
  };

  // Calculate total cars in the parking
  const totalCarsInPark = allCars ? allCars.length : 0;

  return (
    <Card className="glass-effect border-0 shadow-xl">
      <CardHeader className="p-6 border-b border-slate-100">
        <CardTitle className="text-lg font-bold text-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Stato Parcheggio
          </div>
          <div className="flex items-center text-sm text-slate-600 bg-slate-100 px-3 py-1 rounded-full">
            <Car className="w-4 h-4 mr-1" />
            <span className="font-medium">{totalCarsInPark}</span> auto
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        {tettoie.length > 0 ? (
          <div className="space-y-6">
            {tettoie.map((tettoia) => {
              const sezioniInTettoia = sezioni.filter(s => s.tettoia_id === tettoia.id);
              
              // Calculate total cars in this tettoia
              const totalCarsInTettoia = sezioniInTettoia.reduce((total, sezione) => {
                const campateInSezione = campate.filter(c => c.sezione_id === sezione.id);
                return total + campateInSezione.reduce((sezTotal, campata) => 
                  sezTotal + getCarCountForCampata(campata.id), 0);
              }, 0);

              return (
                <div key={tettoia.id}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-semibold text-slate-800">{tettoia.nome}</h3>
                    <span className="text-sm text-slate-600 bg-blue-50 px-2 py-1 rounded-full">
                      {totalCarsInTettoia} auto
                    </span>
                  </div>
                  <div className="space-y-4 pl-4 border-l-2 border-slate-200">
                    {sezioniInTettoia.map(sezione => {
                      const campateInSezione = campate.filter(c => c.sezione_id === sezione.id);
                      
                      // Calculate total cars in this sezione
                      const totalCarsInSezione = campateInSezione.reduce((total, campata) => 
                        total + getCarCountForCampata(campata.id), 0);

                      return (
                        <div key={sezione.id}>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-slate-700">{sezione.nome}</h4>
                            <span className="text-xs text-slate-600 bg-green-50 px-2 py-1 rounded-full">
                              {totalCarsInSezione} auto
                            </span>
                          </div>
                          {/* UPDATED: Changed from div to button for clickable campata boxes */}
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {campateInSezione.map((campata) => {
                              const carCount = getCarCountForCampata(campata.id);
                              return (
                                <button
                                  key={campata.id}
                                  onClick={() => onCampataClick && onCampataClick(campata)}
                                  className={`p-3 rounded-lg border-2 text-center transition-all duration-200 min-w-[100px] cursor-pointer
                                    hover:shadow-md hover:scale-105 active:scale-95 ${
                                    carCount > 0
                                      ? 'border-orange-200 bg-orange-50 hover:bg-orange-100'
                                      : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
                                  }`}
                                >
                                  <div className="flex items-center justify-center mb-1">
                                    <div className="flex items-center">
                                      <Car className={`w-4 h-4 mr-1 ${
                                        carCount > 0 ? 'text-orange-600' : 'text-slate-400'
                                      }`} />
                                      <span className={`text-sm font-bold ${
                                        carCount > 0 ? 'text-orange-800' : 'text-slate-500'
                                      }`}>
                                        {carCount}
                                      </span>
                                    </div>
                                  </div>
                                  <span className="text-xs font-medium text-slate-800 block leading-tight break-words">
                                    {campata.nome}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Nessuna struttura configurata</p>
          </div>
        )}

        {/* Summary footer */}
        {campate.length > 0 && (
          <div className="mt-6 pt-4 border-t border-slate-200">
            <div className="flex items-center justify-between text-sm text-slate-600">
              <span>Totale campate: {campate.length}</span>
              <span>Campate con auto: {campate.filter(c => getCarCountForCampata(c.id) > 0).length}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}